'use client'

import { FormSubmit, useFormContext } from '@ariakit/react'
import { ComponentProps, useContext, useRef } from 'react'
import styled from 'styled-components'

import { Button, untilMd } from '@syconium/little-miss-figgy'

import { ProductCatalogVariant } from '../../../__generated__/graphql/catalog/graphql'
import { useMakeCartItem } from '../../../brunswick/containers/cart/utils'
import { CartErrorMessage } from '../../../components/atoms/CartErrorMessage/CartErrorMessage'
import {
  WaitlistModalProvider,
  useWaitlistModal,
} from '../../../components/pages/products/ProductPage/WaitlistModal/WaitlistModal'
import { trackEvent } from '../../../lib/analytics'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../_providers/CartProvider.client'
import { useFeatureFlag } from '../../_providers/ExperimentationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { useNextDialogContext } from '../dialogs/NextDialog.client'
import { FormSubmitContent } from '../form/FormSubmitContent'

import { VariantSelectionFormContext, VariantSelectionFormState } from './VariantSelectionForm'

type VariantSelectionFormPurchasingSubmitProps = {
  isPrimaryCtaForPageView: boolean
  callToActionColor?: React.CSSProperties['color']
} & ComponentProps<typeof Button>

export const VariantSelectionFormPurchasingSubmit = (
  props: VariantSelectionFormPurchasingSubmitProps
) => {
  const variantSelectionContext = useContext(VariantSelectionFormContext)!
  return (
    <WaitlistModalProvider
      colorDisplayValue={variantSelectionContext.variantGroup.color?.displayValue ?? ''}
      variantShopifyId={variantSelectionContext.selectedVariant?.shopifyId}
      productTitle={variantSelectionContext.variantGroup.title}
    >
      <VariantSelectionFormPurchasingSubmitImpl {...props} />
    </WaitlistModalProvider>
  )
}

const actionableAvailabilities: Array<ProductCatalogVariant['availability']> = [
  'AVAILABLE',
  'WAIT_LISTING',
]

const VariantSelectionFormPurchasingSubmitImpl = ({
  isPrimaryCtaForPageView,
  callToActionColor,
  ...buttonProps
}: VariantSelectionFormPurchasingSubmitProps) => {
  const {
    selectedVariant,
    product,
    variantGroup,
    trackingCategory,
    loading: selectionLoading,
  } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const makeCartItem = useMakeCartItem()
  const dialogContext = useNextDialogContext()
  const { addItems, status: cartStatus, errorMessage: cartErrorMessage } = useCart()
  const { open: openCartDialog } = useCartDialog()
  const { open: openCartInterstitialDialog } = useCartInterstitialDialog()
  const { openWaitlistModal } = useWaitlistModal()
  const submittingToCartRef = useRef(false)

  /*
  This is a little hacky, but we do this so this button is not shown as loading on
  page load or if a different add to cart is pressed on the same page as this.
  */
  if (cartStatus === 'resolved') {
    submittingToCartRef.current = false
  }
  const loading =
    selectionLoading || (submittingToCartRef.current === true && cartStatus === 'pending')

  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isPrimaryCtaForPageView,
  })

  const formContext = useFormContext()
  const fit = formContext!.useValue<VariantSelectionFormState['fit']>('fit') ?? ''
  const size = formContext!.useValue<VariantSelectionFormState['size']>('size') ?? ''

  const addToBagCtaCopyTestDecision = useFeatureFlag<
    'atb-cta-copy-test-control' | 'atb-cta-copy-test-variant'
  >({
    key: 'atb-cta-copy-test',
    controlVariant: 'atb-cta-copy-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: size.length > 0 || variantGroup.availability !== 'AVAILABLE' || !isPrimaryCtaForPageView,
  })

  const allVariantFieldsFilled = size.length > 0 && fit.length > 0
  const notFound = allVariantFieldsFilled && !selectedVariant
  const disabledAvailability =
    allVariantFieldsFilled &&
    selectedVariant?.availability &&
    !actionableAvailabilities.includes(selectedVariant.availability)
  const disabled = loading || notFound || disabledAvailability

  formContext?.useSubmit(() => {
    if (selectedVariant) {
      if (selectedVariant.availability === 'AVAILABLE') {
        submittingToCartRef.current = true
        addItems([
          makeCartItem({
            product,
            variant: selectedVariant,
            variantGroup,
            options: {
              attributedPortal: undefined,
            },
          }),
        ])
        if (!cartErrorMessage) {
          dialogContext?.hide()
          if (cartInterstitialTestDecision === 'yes-cart-interstitial') {
            openCartInterstitialDialog()
          } else {
            openCartDialog()
          }
        }
      } else if (selectedVariant.availability === 'WAIT_LISTING') {
        openWaitlistModal()
      }
    }
  })

  const addToBagContent = buttonProps.children ?? translations.detailsSection.addToBagCTA
  const incompleteFormContent =
    addToBagCtaCopyTestDecision === 'atb-cta-copy-test-variant' && isPrimaryCtaForPageView
      ? translations.detailsSection.selectSizeAddToBagButtonCTA
      : addToBagContent

  return (
    <FormSubmit
      render={htmlProps => (
        <>
          <Button
            {...trackEvent({
              action: 'click',
              category: trackingCategory,
              label: 'Add to Bag',
            })}
            disabled={disabled}
            block
            size='large'
            callToActionColor={callToActionColor}
            {...htmlProps}
            {...buttonProps}
          >
            <FormSubmitContent
              isSubmitting={loading}
              submittingLabel={translations.magnolia.general.loading}
            >
              {!allVariantFieldsFilled && !disabled
                ? incompleteFormContent
                : notFound
                ? translations.detailsSection.soldOutAddToBagCTA
                : selectedVariant?.availability === 'WAIT_LISTING'
                ? translations.detailsSection.waitlistAddToBagCTA
                : selectedVariant?.availability === 'AVAILABLE'
                ? addToBagContent
                : translations.detailsSection.soldOutAddToBagCTA}
            </FormSubmitContent>
          </Button>
          {cartErrorMessage && (
            <ErrorMessageContainer>
              <CartErrorMessage errorMessage={cartErrorMessage} />
            </ErrorMessageContainer>
          )}
        </>
      )}
    />
  )
}

const ErrorMessageContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};

  ${untilMd} {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`
